/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { client } from '@/sanity/client';
import { TestimonialCard } from '../ui/TestimonialCard';
import { ChevronRight } from 'lucide-react';

export const Testimonials = () => {
	const [activeSlide, setActiveSlide] = useState(0);
	const [testimonials, setTestimonials] = useState<any[]>([]);
	const [isMobile, setIsMobile] = useState(false);

	// Update isMobile state on window resize
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkMobile(); // Check on initial render
		window.addEventListener('resize', checkMobile);

		return () => {
			window.removeEventListener('resize', checkMobile);
		};
	}, []);

	useEffect(() => {
		const fetchTestimonials = async () => {
			const data = await client.fetch(`*[_type == "homePage"][0].testimonials{
                title,
                subtitle,
                testimonials[]{
                    name,
                    role,
                    quote,
                    image,
                    rating
                }
            }`);
			setTestimonials(data.testimonials);
		};

		fetchTestimonials();
	}, []);

	const nextSlide = () => {
		if (isMobile) {
			// For mobile, move one full card at a time
			setActiveSlide((prev) =>
				prev === testimonials.length - 1 ? 0 : prev + 1,
			);
		} else {
			// For desktop, preserve current behavior
			setActiveSlide((prev) =>
				prev === testimonials.length - 2 ? 0 : prev + 1,
			);
		}
	};

	if (!testimonials.length) {
		return <div>Loading...</div>;
	}

	// Calculate appropriate translation percentage based on device
	const translateValue = isMobile
		? `${activeSlide * 100}%` // Full width on mobile
		: `${activeSlide * 33.33}%`; // One-third width on desktop

	return (
		<section className='flex flex-col justify-center items-center px-20 py-14 w-full rounded-none max-md:px-5 max-md:max-w-full'>
			<div className='w-full max-w-[1328px] max-md:max-w-full md:px-25'>
				<div className='flex justify-between items-center max-md:flex-col'>
					<header className='max-w-full text-neutral-800 w-[427px]'>
						<h1 className='text-4xl font-bold leading-[40px] max-md:text-[30px] max-md:leading-[40px]'>
							What people <br />
							are saying.
						</h1>
						<h2 className='mt-6 text-2xl leading-snug'>
							Don't take our word for it.
						</h2>
					</header>

					<div className='flex w-[201px] max-w-full items-center gap-5 justify-between max-md:mt-4 max-md:self-start max-md:ml-2.5'>
						<div className='flex items-center gap-2.5 my-auto md:p-2'>
							{/* Show all dots on mobile, but limit on desktop */}
							{testimonials
								.slice(
									0,
									isMobile ? testimonials.length : testimonials.length - 1,
								)
								.map((_, index) => (
									<button
										key={index}
										onClick={() => setActiveSlide(index)}
										className='focus:outline-none'
										aria-label={`Go to testimonial ${index + 1}`}
									>
										<div
											className={`${
												activeSlide === index
													? 'bg-[rgba(251,176,64,1)] h-4 w-4'
													: 'bg-[rgba(251,176,64,0.5)] h-3 w-3'
											} rounded-full transition-all duration-300`}
										/>
									</button>
								))}
						</div>
						<button
							onClick={nextSlide}
							className='flex items-center justify-center bg-[rgba(251,176,64,1)] rounded-full w-12 h-12 text-white transition-transform hover:scale-105'
							aria-label='Next testimonial'
						>
							<ChevronRight className='h-6 w-6' />
						</button>
					</div>
				</div>

				<div className='w-full mt-[27px] max-md:max-w-full relative overflow-hidden'>
					<div
						className='flex transition-all duration-500 ease-in-out'
						style={{ transform: `translateX(-${translateValue})` }}
					>
						{testimonials.map((testimonial, index) => (
							<div
								key={index}
								className='w-full md:w-1/3 flex-shrink-0 px-2.5 md:px-0'
							>
								<div className='flex items-center justify-center mt-[21px] max-md:mt-10 md:pr-4 '>
									<TestimonialCard
										name={testimonial.name}
										company={testimonial.role}
										testimonial={testimonial.quote}
									/>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};
